<template>
  <div class="i-income-potential-chart">
    <div class="title-and-dropdown">
      <div class="title">Your Income Potential as:</div>
      <div class="dropdown">
        <div
          class="selector"
          @click="data.showDropdown = !data.showDropdown"
        >
          <div>Compare up to {{ data.maxSelections }} careers</div>
          <div
            class="icon"
            :class="{ 'is-open': data.showDropdown }"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="13"
              height="8"
              viewBox="0 0 13 8"
              fill="none"
            >
              <path
                d="M1 1L6.5 6.5L12 1"
                stroke="black"
              />
            </svg>
          </div>
        </div>

        <div
          v-show="data.showDropdown"
          class="list"
        >
          <div
            v-for="job in selectableJobsData"
            :key="job.value"
            class="item"
            :class="{
              'is-active': data.selectedJobs.includes(job),
              'is-disabled':
                (data.selectedJobs.length <= data.minSelections && data.selectedJobs.includes(job)) ||
                (data.selectedJobs.length >= data.maxSelections && !data.selectedJobs.includes(job))
            }"
            @click="toggleSelection(job)"
          >
            <div class="checkbox"></div>
            <div class="label">{{ job.text }}</div>
          </div>
        </div>
        <iOnboardingTooltip
          :value="3"
          :position-left="isMobileOrLarger"
        />
      </div>
    </div>

    <div class="indicators">
      <div
        v-for="(job, index) in data.selectedJobs"
        :key="job.value"
        class="item"
      >
        <div
          class="box"
          :style="{ backgroundColor: indicatorColors[index] }"
        ></div>
        <div class="label">{{ job.text }}</div>
      </div>
    </div>

    <div class="chart-card">
      <iLineChart
        class="chart"
        v-bind="chartableData"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive } from "vue"
import { useReportStore } from "@/stores/report"
import { useUserStore } from "@/stores/user"
import { azureRadiance300, bittersweet300, bilbao300 } from "@/styles/colors"
import { breakpointSm } from "@/styles/variables"

import useIsScreenSize from "@/composables/useIsScreenSize"

import iLineChart from "@/components/global/LineChart.vue"
import iOnboardingTooltip from "@/components/report/OnboardingTooltip.vue"

// TYPES
import type { ExtendedRecommendedJob } from "@/types"
type JobData = {
  text: string
  value: number
  salaryData: {
    entry: number
    mid: number
    senior: number
  }
}

type JobDataSet = {
  backgroundColor: string
  borderColor: string
  color: string
  data: number[]
  fill: boolean
  label: string
  pointBackgroundColor: string
  pointBorderColor: string
  pointHoverBackgroundColor: string
  pointHoverBorderColor: string
  radius: number
}

// STORES AND COMPOSABLES
const recommendedJobs = useReportStore().getRecommendedJobs as ExtendedRecommendedJob[]
const isFreeUser = useUserStore().isTierFree
const isMobileOrLarger = useIsScreenSize(breakpointSm)

// CONST

const indicatorColors = [azureRadiance300, bittersweet300, bilbao300]

const sampleJobsData = [
  {
    text: "Sample Job 1",
    value: 9999991,
    salaryData: { entry: 50000, mid: 64000, senior: 74000 }
  },
  {
    text: "Sample Job 2",
    value: 9999992,
    salaryData: { entry: 57000, mid: 66000, senior: 72000 }
  }
] as JobData[]

// DATA
const data = reactive({
  chartableData: {
    chartId: "capboiChart",
    labels: ["Entry", "Median", "Senior"],
    steps: 5
  },
  maxSelections: 3,
  minSelections: 1,
  rangeVariance: 10000,
  selectedJobs: [] as JobData[],
  showDropdown: false
})

// COMPUTED (CHART SPECIFIC)

const chartableData = computed(() => {
  return {
    ...data.chartableData,
    dataSets: [...chartableJobsData.value],
    range: chartableRange.value
  }
})

const chartableRange = computed(() => {
  const min = Math.min(...chartableJobsData.value.map((job) => job.data[0])) - data.rangeVariance
  const max = Math.max(...chartableJobsData.value.map((job) => job.data[job.data.length - 1])) + data.rangeVariance
  return { minimum: min, maximum: max }
})

const chartableJobsData = computed(
  () =>
    data.selectedJobs.map((job, index) => {
      return {
        backgroundColor: indicatorColors[index],
        borderColor: indicatorColors[index],
        color: indicatorColors[index],
        data: [job.salaryData.entry, job.salaryData.mid, job.salaryData.senior],
        fill: false,
        label: job.text,
        pointBackgroundColor: indicatorColors[index],
        pointBorderColor: indicatorColors[index],
        pointHoverBackgroundColor: indicatorColors[index],
        pointHoverBorderColor: indicatorColors[index],
        radius: 3
      }
    }) as JobDataSet[]
)

// COMPUTED (GENERAL)

const filteredJobsData = computed(() => recommendedJobs?.filter((job) => job.isVisible) || [])

const freeUserJobsData = computed(() => [...mappedJobsData.value, ...sampleJobsData])

const mappedJobsData = computed(() =>
  filteredJobsData.value.map(
    (job) =>
      ({
        text: job.title,
        value: job.id,
        salaryData: {
          entry: job.salaryPositions.entry,
          mid: job.salaryPositions.mid,
          senior: job.salaryPositions.senior
        }
      }) as JobData
  )
)

const selectableJobsData = computed(() => (isFreeUser ? freeUserJobsData.value : mappedJobsData.value))

// FUNCTIONS
const setSelectedJobs = () => {
  data.selectedJobs = isFreeUser
    ? freeUserJobsData.value.slice(0, data.maxSelections)
    : mappedJobsData.value.slice(0, data.maxSelections)
}

const toggleSelection = (job: JobData) => {
  const hasMinimumSelected = data.selectedJobs.length <= data.minSelections
  const hasMaximumSelected = data.selectedJobs.length >= data.maxSelections
  if (hasMinimumSelected && data.selectedJobs.includes(job)) return
  if (hasMaximumSelected && !data.selectedJobs.includes(job)) return

  const index = data.selectedJobs.indexOf(job)
  if (index !== -1) {
    data.selectedJobs.splice(index, 1)
  } else {
    data.selectedJobs.push(job)
  }
}

// LIFECYCLE HOOKS
onMounted(async () => {
  setSelectedJobs()
})
</script>

<style lang="scss" scoped>
.i-income-potential-chart {
  padding: 20px;
  width: 100%;
  height: 100%;

  @media screen and (min-width: $breakpoint-xl) {
    border-bottom: 1px solid $zumthor100;
  }

  .title-and-dropdown {
    display: flex;
    flex-direction: column;
    gap: 8px;
    justify-content: space-between;
    margin-bottom: 16px;

    @media screen and (min-width: $breakpoint-sm) {
      align-items: center;
      flex-direction: row;
    }

    .title {
      font-size: 16px;
      font-weight: 600;
    }

    .dropdown {
      align-items: center;
      display: flex;
      position: relative;
      width: 230px;

      .selector {
        align-items: center;
        border-radius: 8px;
        border: 1px solid $comet200;
        cursor: pointer;
        display: flex;
        font-size: 14px;
        gap: 8px;
        justify-content: space-between;
        padding: 4px 12px;
        width: 100%;

        .icon {
          transition: transform 0.3s;

          &.is-open {
            transform: rotate(180deg);
          }
        }
      }

      .list {
        background-color: white;
        border-radius: 5px;
        border: 1px solid $comet200;
        display: flex;
        flex-direction: column;
        font-size: 14px;
        left: 0;
        margin-top: 5px;
        padding: 8px 12px;
        position: absolute;
        top: 32px;
        width: 100%;
        z-index: 1;

        .item {
          align-items: center;
          cursor: pointer;
          display: flex;
          gap: 8px;
          padding-top: 6px;
          padding-bottom: 6px;

          .checkbox {
            border-radius: 4px;
            border: 1px solid $comet200;
            height: 18px;
            min-width: 18px;
            width: 18px;
          }

          .label {
            font-size: 14px;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }

        .item.is-active {
          .checkbox {
            background-color: $azure-radiance500;
            border-radius: 4px;
            border: 1px solid $azure-radiance500;
            height: 18px;
            min-width: 18px;
            width: 18px;
          }

          .checkbox::before {
            align-items: center;
            color: white;
            content: "✔";
            display: flex;
            height: 100%;
            justify-content: center;
            transform: scale(1);
            width: 100%;
          }
        }

        .item.is-disabled {
          color: gray;
          cursor: not-allowed;
        }
      }
    }
  }

  .indicators {
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    align-items: center;
    margin-bottom: 16px;
    min-height: 24px;

    .item {
      align-items: center;
      display: flex;
      gap: 8px;
      margin-right: 24px;
      min-width: 200px;
      max-width: 44%;

      .box {
        border-radius: 4px;
        height: 18px;
        min-width: 18px;
        width: 18px;
      }

      .label {
        font-size: 14px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }
    }
  }

  .chart-card {
    width: 100%;

    .chart-tags {
      display: flex;
      justify-content: center;
      margin-bottom: 8px;

      div {
        margin-left: 4px;
        margin-right: 4px;
      }
    }

    .chart {
      display: block;
    }
  }
}
</style>
